import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Building2, MessageSquareText, BarChart3, Briefcase, FileText, LogOut, Settings, UserCheck, Map, Menu, X, MessageCircle, Target, KeyRound } from 'lucide-react';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';

const Sidebar = () => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);

  const menuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, roles: ['super_admin', 'admin', 'sales_rep', 'front_desk'] },
    { name: 'Leads', path: '/leads', icon: Users, roles: ['super_admin', 'admin', 'sales_rep', 'front_desk'] },
    { name: 'Clients', path: '/clients', icon: UserCheck, roles: ['super_admin', 'admin', 'sales_rep', 'front_desk'] },
    { name: 'Plots', path: '/projects', icon: Map, roles: ['super_admin', 'admin', 'sales_rep'] },
    { name: 'Flats', path: '/properties', icon: Building2, roles: ['super_admin', 'admin', 'sales_rep', 'front_desk'] },
    { name: 'WhatsApp', path: '/whatsapp', icon: MessageCircle, roles: ['super_admin', 'admin', 'sales_rep'] },
    { name: 'Matching', path: '/property-matching', icon: Target, roles: ['super_admin', 'admin', 'sales_rep'] },
    { name: 'Communications', path: '/communications', icon: MessageSquareText, roles: ['super_admin', 'admin', 'sales_rep'] },
    { name: 'Analytics', path: '/analytics', icon: BarChart3, roles: ['super_admin', 'admin'] },
    { name: 'Team', path: '/team', icon: Briefcase, roles: ['super_admin', 'admin'] },
    { name: 'Documents', path: '/documents', icon: FileText, roles: ['super_admin', 'admin', 'sales_rep'] },
    { name: 'User Management', path: '/user-management', icon: Users, roles: ['super_admin', 'admin'] },
    { name: 'Settings', path: '/settings', icon: Settings, roles: ['super_admin', 'admin'] },
  ];

  const filteredItems = menuItems.filter((item) => !item.roles || item.roles.includes(user?.role));

  const navContent = (
    <>
      {/* Brand */}
      <div className="px-5 py-5 border-b border-white/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Aavaas" className="w-9 h-9 rounded-xl object-contain" />
          <div>
            <h1 className="text-[15px] font-bold text-white tracking-tight">Aavaas</h1>
            <p className="text-[10px] text-white/30 font-semibold tracking-[0.15em] uppercase">Real Estate CRM</p>
          </div>
        </div>
        <button onClick={() => setMobileOpen(false)} className="lg:hidden p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors" data-testid="close-mobile-menu">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {filteredItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              data-testid={`sidebar-${item.name.toLowerCase().replace(/\s/g, '-')}`}
              className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-200 relative ${
                isActive
                  ? 'bg-white/[0.08] text-white'
                  : 'text-white/40 hover:bg-white/[0.05] hover:text-white/70'
              }`}
            >
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-[#B89B72] rounded-r-full" />
              )}
              <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-colors duration-200 ${isActive ? 'text-[#D4B98A]' : 'text-white/25 group-hover:text-white/50'}`} />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* User / Logout */}
      <div className="px-3 py-3 border-t border-white/[0.06]">
        {user && (
          <div className="px-3 py-2 mb-1">
            <p className="text-xs font-semibold text-white/70 truncate">{user.name}</p>
            <p className="text-[10px] text-white/30 capitalize font-medium">{user.role?.replace(/_/g, ' ')}</p>
          </div>
        )}
        <button
          onClick={() => { setShowChangePw(true); setMobileOpen(false); }}
          data-testid="sidebar-change-password-button"
          className="flex items-center gap-3 w-full px-3 py-2.5 text-[13px] font-medium text-white/30 hover:bg-white/[0.05] hover:text-white/60 rounded-lg transition-all duration-200"
        >
          <KeyRound className="w-[18px] h-[18px]" />
          Change Password
        </button>
        <button
          onClick={() => { logout(); setMobileOpen(false); }}
          data-testid="sidebar-logout-button"
          className="flex items-center gap-3 w-full px-3 py-2.5 text-[13px] font-medium text-white/30 hover:bg-white/[0.05] hover:text-white/60 rounded-lg transition-all duration-200"
        >
          <LogOut className="w-[18px] h-[18px]" />
          Logout
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Header Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 safe-area-top shadow-lg" style={{ background: 'hsl(222, 47%, 8%)' }}>
        <button onClick={() => setMobileOpen(true)} className="p-1.5 text-white rounded-lg hover:bg-white/10 transition-colors" data-testid="mobile-menu-button">
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Aavaas" className="w-7 h-7 rounded-lg object-contain" />
          <h1 className="text-sm font-bold text-white tracking-tight">Aavaas CRM</h1>
        </div>
        <NotificationBell position="top" />
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex w-[240px] flex-col h-screen flex-shrink-0" style={{ background: 'hsl(222, 47%, 8%)' }}>
        {navContent}
        <div className="px-3 pb-3">
          <NotificationBell position="bottom" />
        </div>
      </div>

      {/* Mobile Drawer Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 flex flex-col animate-slide-in shadow-2xl" style={{ background: 'hsl(222, 47%, 8%)' }}>
            {navContent}
          </div>
        </div>
      )}
      <ChangePasswordDialog open={showChangePw} onOpenChange={setShowChangePw} />
    </>
  );
};

export default Sidebar;
